import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createAppliance, updateAppliance, getAppliance, getRooms } from '../../utils/api';
import { useApp } from '../../context/AppContext';

const CATEGORIES = ['hvac', 'water-heater', 'washer', 'dryer', 'dishwasher', 'refrigerator', 'oven', 'other'];

const defaultForm = {
  name: '',
  category: 'other',
  room: '',
  make: '',
  model: '',
  serialNumber: '',
  purchaseDate: '',
  installDate: '',
  warrantyExpiry: '',
  notes: '',
};

const toDateInput = (val) => (val ? new Date(val).toISOString().split('T')[0] : '');

const Section = ({ title, children }) => (
  <fieldset className="form-section">
    <legend className="form-section-title">{title}</legend>
    {children}
  </fieldset>
);

const ApplianceForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetchAppliances } = useApp();
  const [form, setForm] = useState(defaultForm);
  const [rooms, setRooms] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const isEditing = Boolean(id);

  useEffect(() => {
    getRooms().then(({ data }) => setRooms(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEditing) return;
    getAppliance(id)
      .then(({ data }) => {
        setForm({
          ...defaultForm,
          ...data,
          room: data.room?._id || '',
          purchaseDate: toDateInput(data.purchaseDate),
          installDate: toDateInput(data.installDate),
          warrantyExpiry: toDateInput(data.warrantyExpiry),
        });
      })
      .catch(() => setLoadError('Appliance not found.'));
  }, [id, isEditing]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, room: form.room || undefined };
      if (isEditing) {
        await updateAppliance(id, payload);
      } else {
        await createAppliance(payload);
      }
      await fetchAppliances({ force: true });
      navigate('/appliances');
    } finally {
      setSaving(false);
    }
  };

  if (loadError) return <div className="page"><p className="empty-state">{loadError}</p></div>;

  return (
    <div className="page room-form-page">
      <div className="page-header">
        <h1>{isEditing ? 'Edit Appliance' : 'New Appliance'}</h1>
        <button className="btn btn-secondary" onClick={() => navigate('/appliances')}>Cancel</button>
      </div>

      <form onSubmit={handleSubmit} className="room-form">
        <Section title="Basic Info">
          <div className="form-row">
            <label>Name *
              <input name="name" value={form.name} onChange={handleChange} required placeholder="e.g. Furnace" />
            </label>
            <label>Category *
              <select name="category" value={form.category} onChange={handleChange}>
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c.replace('-', ' ')}</option>
                ))}
              </select>
            </label>
            <label>Room
              <select name="room" value={form.room} onChange={handleChange}>
                <option value="">— none —</option>
                {rooms.map((r) => (
                  <option key={r._id} value={r._id}>{r.name}</option>
                ))}
              </select>
            </label>
          </div>
        </Section>

        <Section title="Model Info">
          <div className="form-row">
            <label>Make
              <input name="make" value={form.make} onChange={handleChange} placeholder="e.g. Carrier" />
            </label>
            <label>Model
              <input name="model" value={form.model} onChange={handleChange} placeholder="e.g. 58CVA090" />
            </label>
            <label>Serial Number
              <input name="serialNumber" value={form.serialNumber} onChange={handleChange} />
            </label>
          </div>
        </Section>

        <Section title="Dates">
          <div className="form-row">
            <label>Purchase Date
              <input type="date" name="purchaseDate" value={form.purchaseDate} onChange={handleChange} />
            </label>
            <label>Install Date
              <input type="date" name="installDate" value={form.installDate} onChange={handleChange} />
            </label>
            <label>Warranty Expiry
              <input type="date" name="warrantyExpiry" value={form.warrantyExpiry} onChange={handleChange} />
            </label>
          </div>
        </Section>

        <Section title="Notes">
          <label>
            <textarea name="notes" value={form.notes} onChange={handleChange} rows={3} />
          </label>
        </Section>

        <div className="form-actions form-actions-bottom">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving…' : isEditing ? 'Save Changes' : 'Create Appliance'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/appliances')}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default ApplianceForm;
